import { FINGER_TYPE, SHIFT_KEY, HAND_TYPE } from '../../../constants/Consts';
import { keyInfo, charInfo } from '../../../types/Typing';
import { HandElement } from './HandElement';

interface KeyboardProps {
  shiftKeyDown: string;
  inputErrorKey: string | undefined;
  remainingRomaji: string;
}

const baseStyle: string = 'flex justify-center items-center border border-main_key rounded select-none text-main_key font-extrabold';
const baseSize: string = 'w-full aspect-[1/1]';
const errorKeyStyle: string = '!outline !outline-4 !outline-error_key !outline-offset-[-2px]';

const firstRowKeys: keyInfo[] = [
  { handType: undefined, fingerType: undefined },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: '1' }, shiftMode: { inputChar: '!' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.RING, normalMode: { inputChar: '2' }, shiftMode: { inputChar: '"' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.MIDDLE, normalMode: { inputChar: '3' }, shiftMode: { inputChar: '#' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: '4' }, shiftMode: { inputChar: '$' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: '5' }, shiftMode: { inputChar: '%' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: '6' }, shiftMode: { inputChar: '&' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: '7' }, shiftMode: { inputChar: "'" } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.MIDDLE, normalMode: { inputChar: '8' }, shiftMode: { inputChar: '(' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.RING, normalMode: { inputChar: '9' }, shiftMode: { inputChar: ')' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: '0' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: '-' }, shiftMode: { inputChar: '=' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: '^' }, shiftMode: { inputChar: '~' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: '¥' }, shiftMode: { inputChar: '|' } },
  { handType: undefined, fingerType: undefined },
];

const secondRowKeys: keyInfo[] = [
  { handType: undefined, fingerType: undefined, addKeyStyle: 'w-[150%]' },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: 'q', displayChar: 'Q' }, shiftMode: { inputChar: 'Q' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.RING, normalMode: { inputChar: 'w', displayChar: 'W' }, shiftMode: { inputChar: 'W' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.MIDDLE, normalMode: { inputChar: 'e', displayChar: 'E' }, shiftMode: { inputChar: 'E' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: 'r', displayChar: 'R' }, shiftMode: { inputChar: 'R' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: 't', displayChar: 'T' }, shiftMode: { inputChar: 'T' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: 'y', displayChar: 'Y' }, shiftMode: { inputChar: 'Y' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: 'u', displayChar: 'U' }, shiftMode: { inputChar: 'U' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.MIDDLE, normalMode: { inputChar: 'i', displayChar: 'I' }, shiftMode: { inputChar: 'I' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.RING, normalMode: { inputChar: 'o', displayChar: 'O' }, shiftMode: { inputChar: 'O' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: 'p', displayChar: 'P' }, shiftMode: { inputChar: 'P' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: '@' }, shiftMode: { inputChar: '`' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: '[' }, shiftMode: { inputChar: '{' } },
];

const thirdRowKeys: keyInfo[] = [
  { handType: undefined, fingerType: undefined, addKeyStyle: 'w-[150%]' },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: 'a', displayChar: 'A' }, shiftMode: { inputChar: 'A' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.RING, normalMode: { inputChar: 's', displayChar: 'S' }, shiftMode: { inputChar: 'S' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.MIDDLE, normalMode: { inputChar: 'd', displayChar: 'D' }, shiftMode: { inputChar: 'D' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: 'f', displayChar: 'F' }, shiftMode: { inputChar: 'F' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: 'g', displayChar: 'G' }, shiftMode: { inputChar: 'G' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: 'h', displayChar: 'H' }, shiftMode: { inputChar: 'H' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: 'j', displayChar: 'J' }, shiftMode: { inputChar: 'J' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.MIDDLE, normalMode: { inputChar: 'k', displayChar: 'K' }, shiftMode: { inputChar: 'K' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.RING, normalMode: { inputChar: 'l', displayChar: 'L' }, shiftMode: { inputChar: 'L' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: ';' }, shiftMode: { inputChar: '+' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: ':' }, shiftMode: { inputChar: '*' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: ']' }, shiftMode: { inputChar: '}' } },
];

const fourthRowKeys: keyInfo[] = [
  {
    handType: HAND_TYPE.LIFT,
    fingerType: FINGER_TYPE.LITTLE,
    normalMode: { inputChar: SHIFT_KEY, displayChar: 'シフト' },
    shiftMode: { inputChar: SHIFT_KEY, displayChar: 'シフト' },
    addKeyStyle: 'w-[200%]',
    id: 'shift-key-left',
  },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: 'z', displayChar: 'Z' }, shiftMode: { inputChar: 'Z' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.RING, normalMode: { inputChar: 'x', displayChar: 'X' }, shiftMode: { inputChar: 'X' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.MIDDLE, normalMode: { inputChar: 'c', displayChar: 'C' }, shiftMode: { inputChar: 'C' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: 'v', displayChar: 'V' }, shiftMode: { inputChar: 'V' } },
  { handType: HAND_TYPE.LIFT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: 'b', displayChar: 'B' }, shiftMode: { inputChar: 'B' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: 'n', displayChar: 'N' }, shiftMode: { inputChar: 'N' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.INDEX, normalMode: { inputChar: 'm', displayChar: 'M' }, shiftMode: { inputChar: 'M' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.MIDDLE, normalMode: { inputChar: ',' }, shiftMode: { inputChar: '<' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.RING, normalMode: { inputChar: '.' }, shiftMode: { inputChar: '>' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: '/' }, shiftMode: { inputChar: '?' } },
  { handType: HAND_TYPE.RIGHT, fingerType: FINGER_TYPE.LITTLE, normalMode: { inputChar: '\\' }, shiftMode: { inputChar: '_' } },
  {
    handType: HAND_TYPE.RIGHT,
    fingerType: FINGER_TYPE.LITTLE,
    normalMode: { inputChar: SHIFT_KEY, displayChar: 'シフト' },
    shiftMode: { inputChar: SHIFT_KEY, displayChar: 'シフト' },
    addKeyStyle: 'w-[200%]',
    id: 'shift-key-right',
  },
];

const fifthRowKeys: keyInfo[] = [
  { handType: undefined, fingerType: undefined },
  { handType: undefined, fingerType: undefined },
  { handType: undefined, fingerType: undefined },
  { handType: undefined, fingerType: undefined },
  { handType: undefined, fingerType: undefined },
  {
    handType: HAND_TYPE.BOTH,
    fingerType: FINGER_TYPE.THUMB,
    normalMode: { inputChar: ' ', displayChar: 'スペース' },
    // NOTE:スペースはシフト不要のため shiftMode の inputChar を設定しない
    shiftMode: { displayChar: 'スペース' },
    addKeyStyle: 'w-[455%]',
    id: 'space-key',
  },
  { handType: undefined, fingerType: undefined },
  { handType: undefined, fingerType: undefined },
  { handType: undefined, fingerType: undefined },
  { handType: undefined, fingerType: undefined },
  { handType: undefined, fingerType: undefined },
  { handType: undefined, fingerType: undefined },
];

const allRowKeys: keyInfo[][] = [firstRowKeys, secondRowKeys, thirdRowKeys, fourthRowKeys, fifthRowKeys];
const enterKeyInfo: keyInfo = { handType: undefined, fingerType: undefined, addKeyStyle: 'w-[11%]' };
const usingShiftKeys = allRowKeys
  .flat()
  .filter((rowKeys) => rowKeys.shiftMode?.inputChar)
  .map((x) => x.shiftMode?.inputChar);

export const Keyboard = ({ shiftKeyDown, inputErrorKey, remainingRomaji }: KeyboardProps) => {
  const isShiftMode = shiftKeyDown.length > 0;
  const currentChar: string = remainingRomaji?.length > 0 ? remainingRomaji[0] : remainingRomaji;
  const currentKeyInfo: keyInfo | undefined = allRowKeys
    .flatMap((keyInfo) => keyInfo)
    .find((x) => x.normalMode?.inputChar === currentChar || x.shiftMode?.inputChar === currentChar);

  const getKeyChar = (keyInfo: keyInfo) => {
    const targetCharInfo: charInfo | undefined = isShiftMode ? keyInfo.shiftMode : keyInfo.normalMode;
    if (!targetCharInfo) {
      return null;
    }
    return targetCharInfo.displayChar ?? targetCharInfo.inputChar;
  };

  const getKeyStyle = (keyInfo: keyInfo) => {
    let fingerStyle = `${keyInfo.addKeyStyle ?? baseSize}`;

    // シフトキーを使った入力の場合
    if (usingShiftKeys.includes(currentChar)) {
      if (keyInfo.shiftMode?.inputChar === currentChar) {
        return `${fingerStyle} ${!isShiftMode ? 'bg-using_shift_key' : 'bg-current_key'}`;
      } else if (keyInfo.shiftMode?.inputChar === SHIFT_KEY && keyInfo.handType !== currentKeyInfo?.handType) {
        return `${fingerStyle} ${isShiftMode ? 'bg-using_shift_key' : 'bg-current_key'}`;
      }
    }

    const targetCharInfo: charInfo | undefined = isShiftMode ? keyInfo.shiftMode : keyInfo.normalMode;

    // 空白キーの場合
    if (!targetCharInfo) {
      return `${fingerStyle} bg-blank_key`;
    }

    // 正解キーの場合
    if (targetCharInfo.inputChar === currentChar) {
      return `${fingerStyle} bg-current_key`;
    }

    // 指ごとの色
    switch (keyInfo.fingerType) {
      case FINGER_TYPE.LITTLE:
        fingerStyle = `${fingerStyle} bg-little_finger`;
        break;
      case FINGER_TYPE.RING:
        fingerStyle = `${fingerStyle} bg-ring_finger`;
        break;
      case FINGER_TYPE.MIDDLE:
        fingerStyle = `${fingerStyle} bg-middle_finger`;
        break;
      case FINGER_TYPE.INDEX:
        fingerStyle = `${fingerStyle} bg-index_finger`;
        break;
      case FINGER_TYPE.THUMB:
        fingerStyle = `${fingerStyle} bg-thumbs_finger`;
        break;
    }

    // 不正解キーの場合
    if (inputErrorKey && targetCharInfo?.inputChar === inputErrorKey) {
      fingerStyle = `${fingerStyle} ${errorKeyStyle}`;
    }

    return fingerStyle;
  };

  return (
    <>
      <div className="flex flex-col items-center justify-center w-[80%] mx-auto p-[1%] border-4 border-[#cb9b0a] bg-white rounded-2xl mt-[3%]">
        <div className="flex w-full gap-[1%]">
          {firstRowKeys.map((keyInfo, index) => {
            return (
              <div
                key={`1_${index}`}
                id={keyInfo.id}
                className={`${baseStyle} ${getKeyStyle(keyInfo)}`}
              >
                {getKeyChar(keyInfo)}
              </div>
            );
          })}
        </div>
        <div className="w-full flex my-[1%]">
          <div className="w-full mr-[1%]">
            <div className="flex w-full gap-[1%] mb-[1%]">
              {secondRowKeys.map((keyInfo, index) => {
                return (
                  <div
                    key={`2_${index}`}
                    id={keyInfo.id}
                    className={`${baseStyle} ${getKeyStyle(keyInfo)}`}
                  >
                    {getKeyChar(keyInfo)}
                  </div>
                );
              })}
            </div>
            <div className="flex w-full gap-[1%]">
              {thirdRowKeys.map((keyInfo, index) => {
                return (
                  <div
                    key={`3_${index}`}
                    id={keyInfo.id}
                    className={`${baseStyle} ${getKeyStyle(keyInfo)}`}
                  >
                    {getKeyChar(keyInfo)}
                  </div>
                );
              })}
            </div>
          </div>
          <div className={`${baseStyle} ${getKeyStyle(enterKeyInfo)}`} />
        </div>
        <div className="flex w-full gap-[1%] mb-[1%]">
          {fourthRowKeys.map((keyInfo, index) => {
            return (
              <div
                key={`4_${index}`}
                id={keyInfo.id}
                className={`${baseStyle} ${getKeyStyle(keyInfo)}`}
              >
                {getKeyChar(keyInfo)}
              </div>
            );
          })}
        </div>
        <div className="flex w-full gap-[1%]">
          {fifthRowKeys.map((keyInfo, index) => {
            return (
              <div
                key={`5_${index}`}
                id={keyInfo.id ? `${keyInfo.id}` : `5_${index}`}
                className={`${baseStyle} ${getKeyStyle(keyInfo)}`}
              >
                {getKeyChar(keyInfo)}
              </div>
            );
          })}
        </div>
      </div>
      <div className="absolute bottom-[24%] w-full flex items-center justify-evenly">
        <HandElement
          width="20%"
          handType={HAND_TYPE.LIFT}
          currentKeyInfo={currentKeyInfo}
          currentChar={currentChar}
          usingShiftKeys={usingShiftKeys}
          isShiftMode={isShiftMode}
        />
        <HandElement
          width="20%"
          handType={HAND_TYPE.RIGHT}
          currentKeyInfo={currentKeyInfo}
          currentChar={currentChar}
          usingShiftKeys={usingShiftKeys}
          isShiftMode={isShiftMode}
        />
      </div>
    </>
  );
};
